import { ChatCircleIcon } from "@phosphor-icons/react/dist/csr/ChatCircle";
import { FileMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/FileMagnifyingGlass";
import { GearSixIcon } from "@phosphor-icons/react/dist/csr/GearSix";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { inputTitle, useReportInputs } from "@/profitability/missing-value";
import { shortPeriod } from "@/profitability/new-run/run-period";
import type { RecordObject } from "@/profitability/records/objects";
import { recordTitle } from "@/profitability/records/objects";
import { profitabilityTemplateSettingsPath } from "@/profitability/routes";
import { isSourced } from "@/profitability/sources/sourced";
import { useBlankLines } from "@/profitability/sources/use-blank-lines";
import type { ReportDates, ReportRow } from "@/profitability/types";
import { type FigureGaps, gapsImpact, gapsTitle } from "./figure-gaps";
import {
  missingFields,
  missingImpact,
  missingQuestion,
  missingTitle,
  partialImpact,
  partialTitle,
} from "./missing-fields";
import { UploadInputButton } from "./upload-input-button";

/** Where a record's row came from: its period, and the run that read it. */
export interface RecordPeriod {
  dates: ReportDates | null;
  /** The run whose files to keep when one is added; null when not known. */
  runId: string | null;
}

/**
 * A record missing figures says so first, calmly: which aren't in yet, what
 * they keep from being worked out, and how to fill them: upload the file
 * they come from for this period, or ask Volca why. Where the run traced how
 * its figures were made (`gaps`), it names only the root gaps, each with
 * why (a blank cell, every line blank, nothing matched, a setting not set)
 * and its own upload, and the figures worked out from them as what they
 * block: Burden is missing because Labor is, not as a gap of its own. Else
 * it reads both from the roles the report declares. A figure that has a
 * value but left blank lines of its file out ("1 of 3 blank") is said too,
 * since its value hides the gap. Never a zero (FIN-004), and never a figure
 * typed in by hand, which the files could no longer account for.
 */
export const RecordMissing = ({
  businessId,
  reportId,
  object,
  row,
  period,
  gaps = null,
  pending = false,
  canUpload,
  onAsk,
  onShowSource,
}: {
  businessId: string;
  reportId: string;
  object: RecordObject;
  row: ReportRow;
  period: RecordPeriod | null;
  /** The missing figures split into root gaps and what waits on them (`useRecordGaps`). */
  gaps?: FigureGaps | null;
  /** While `gaps` loads: say nothing rather than say it the other way first. */
  pending?: boolean;
  /** Only someone who may run the template is offered the upload. */
  canUpload: boolean;
  onAsk?: (question: string) => void;
  /** Opens the file a figure (a column's key) was read from, at its blank cell. */
  onShowSource?: (column: string) => void;
}) => {
  const inputs = useReportInputs();
  const [params] = useSearchParams();
  const fields = missingFields(object.dataset, row);
  // Read only where the page can show where figures come from.
  const partials = useBlankLines(
    businessId,
    onShowSource ? period?.runId : null,
    object,
    row,
  );
  if (pending) return null;
  const roots = gaps ? gaps.roots.map(({ column }) => column) : fields.gaps;
  if (!roots.length && !partials.length) return null;
  // The first gap that can show where it came from: its blank cell, or its
  // lines, is where to look; else the first figure with blank lines.
  const sourced = onShowSource
    ? (roots.find((column) => isSourced(object.dataset, column)) ??
      partials[0]?.column)
    : undefined;
  const partialSources = new Set(
    partials.map(({ column }) => column.source ?? ""),
  );
  const [partialSource] = [...partialSources];
  const source = roots.length
    ? fields.source
    : partialSources.size === 1 && partialSource
      ? partialSource
      : null;
  const titleOf = (key: string | null) => inputTitle(inputs, key);
  // The inputs that fill the root gaps, one upload each; a gap that names
  // none (and is not a setting) falls back to adding files for the period.
  const uploads = gaps?.roots.length
    ? [
        ...new Set(
          gaps.roots.flatMap(({ input }) => (titleOf(input) ? [input] : [])),
        ),
      ]
    : [source];
  if (
    gaps?.roots.length &&
    !uploads.length &&
    gaps.roots.some(({ reason }) => reason.kind !== "setting")
  )
    uploads.push(null);
  const setting = gaps?.roots.some(({ reason }) => reason.kind === "setting");
  const runPeriod = params.get("period");
  const settingsHref = `${profitabilityTemplateSettingsPath(reportId)}${runPeriod ? `?${new URLSearchParams({ period: runPeriod }).toString()}` : ""}`;
  const dates = period?.dates ?? null;
  const when = dates ? shortPeriod(dates) : null;
  const ask = roots.length ? onAsk : undefined;
  const title = gaps?.roots.length
    ? gapsTitle(gaps)
    : fields.gaps.length
      ? missingTitle(fields)
      : partialTitle(partials);
  const impact = gaps?.roots.length
    ? gapsImpact(gaps, titleOf)
    : fields.gaps.length
      ? missingImpact(fields, titleOf(source))
      : null;
  return (
    // A section of the sheet, so its divider sits under the note, not on it.
    <section aria-label="Missing figures">
      <Alert color="warning" role="status">
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          <Stack gap={2} align="start">
            {impact && <span>{impact}</span>}
            {partials.map((partial) => (
              <span key={partial.column.key}>
                {partialImpact(partial, titleOf(partial.column.source ?? null))}
              </span>
            ))}
            {(canUpload || ask || sourced) && (
              <Stack direction="row" gap={2} className="flex-wrap">
                {canUpload &&
                  uploads.map((input) => (
                    <UploadInputButton
                      key={input ?? ""}
                      reportId={reportId}
                      source={input}
                      dates={dates}
                      runId={period?.runId}
                    />
                  ))}
                {canUpload && setting && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="no-underline!"
                    nativeButton={false}
                    render={<Link to={settingsHref} />}
                  >
                    <GearSixIcon aria-hidden data-icon="inline-start" />
                    Open Settings
                  </Button>
                )}
                {ask && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      ask(
                        missingQuestion(
                          { gaps: roots },
                          recordTitle(object, row),
                          when,
                        ),
                      )
                    }
                  >
                    <ChatCircleIcon aria-hidden data-icon="inline-start" />
                    Ask Volca
                  </Button>
                )}
                {sourced && onShowSource && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onShowSource(sourced.key)}
                  >
                    <FileMagnifyingGlassIcon
                      aria-hidden
                      data-icon="inline-start"
                    />
                    Show in the file
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </AlertDescription>
      </Alert>
    </section>
  );
};
